/**
 * @file IconButton.ts
 * @role Reusable small round icon button (back, settings, rules, etc.). Render + input
 *       only. Uses a glyph string as a placeholder until real icon textures land in the
 *       `ui` asset bundle — the component contract stays the same when art arrives.
 * @owns Its circular background, glyph, hit state.
 * @reads Nothing.
 * @neverDoes Call backend. Decide game flow. Load assets directly.
 * @invariants 8 (views render only), 15 (textures will come via AssetService later).
 * @see docs/UI_LAYOUT_GUIDE.md
 */
import { Container, Graphics, RoundedRectangle, Sprite, Text, type Texture } from 'pixi.js';

const BACKGROUND_ART_COVER_SCALE = 1;

export interface IconButtonOptions {
  glyph: string;
  diameter?: number;
  fill?: number;
  glyphColor?: number;
  texture?: Texture | undefined;
  backgroundTexture?: Texture | undefined;
  iconScaleRatio?: number;
  iconOffsetX?: number;
  iconOffsetY?: number;
  activeIconScaleRatio?: number;
  activeIconTint?: number;
  accessibleTitle?: string;
  accessibleHint?: string;
  tabIndex?: number;
}

export class IconButton extends Container {
  private readonly dropShadow = new Graphics();
  private readonly bg = new Graphics();
  private readonly backgroundSprite = new Sprite();
  private readonly backgroundMask = new Graphics();
  readonly contentLayer = new Container();
  private readonly sprite = new Sprite();
  private readonly glyphText: Text;
  private readonly diameter: number;
  private readonly iconScaleRatio: number;
  private readonly iconOffsetX: number;
  private readonly iconOffsetY: number;
  private readonly activeIconScaleRatio: number;
  private readonly activeIconTint: number;
  private hasBackgroundTexture = false;
  private handler: (() => void) | undefined;
  private activationHandler: (() => void) | undefined;
  private isEnabled = true;
  private isActive = false;
  private isHovered = false;
  private isPressed = false;
  private layoutScale = 1;
  private contentScale = 1;
  private accessibilityEnabled = true;
  private readonly hasAccessibleLabel: boolean;

  constructor(options: IconButtonOptions) {
    super();
    this.diameter = options.diameter ?? 56;
    this.iconScaleRatio = options.iconScaleRatio ?? 0.5;
    this.iconOffsetX = options.iconOffsetX ?? 0;
    this.iconOffsetY = options.iconOffsetY ?? 0;
    this.activeIconScaleRatio = options.activeIconScaleRatio ?? 1;
    this.activeIconTint = options.activeIconTint ?? 0xffffff;
    const fill = options.fill ?? 0x1c1c2a;
    this.hasAccessibleLabel = Boolean(options.accessibleTitle);

    this.dropShadow
      .circle(0, this.diameter * 0.09, this.diameter / 2)
      .fill({ color: 0x000000, alpha: 0.42 });
    this.dropShadow.visible = false;
    this.dropShadow.eventMode = 'none';
    this.bg.circle(0, 0, this.diameter / 2).fill({ color: fill });
    this.backgroundSprite.anchor.set(0.5);
    this.backgroundSprite.visible = false;
    this.backgroundMask.circle(0, 0, this.diameter / 2).fill({ color: 0xffffff });
    this.backgroundSprite.mask = this.backgroundMask;
    this.sprite.anchor.set(0.5);
    this.sprite.position.set(this.iconOffsetX, this.iconOffsetY);
    this.sprite.visible = false;
    this.glyphText = new Text({
      text: options.glyph,
      style: { fill: options.glyphColor ?? 0xf5d76e, fontSize: this.diameter * 0.5 },
    });
    this.glyphText.anchor.set(0.5);
    // Optical offsets compensate for asymmetric texture alpha mass. Fallback glyphs have their
    // own text bounds + centered anchor, so inheriting a texture correction would misalign them.
    this.glyphText.position.set(0, 0);

    this.contentLayer.addChild(this.sprite, this.glyphText);
    this.addChild(
      this.dropShadow,
      this.bg,
      this.backgroundSprite,
      this.backgroundMask,
      this.contentLayer,
    );
    this.setBackgroundTexture(options.backgroundTexture);
    if (options.texture) this.setTexture(options.texture);

    this.eventMode = 'static';
    const hitRadius = this.diameter / 2;
    // A full-radius square is geometrically circular while retaining explicit bounds for
    // Pixi's DOM accessibility overlay (Circle only exposes contains(), not width/height).
    this.hitArea = new RoundedRectangle(
      -hitRadius,
      -hitRadius,
      this.diameter,
      this.diameter,
      hitRadius,
    );
    this.accessible = this.hasAccessibleLabel;
    this.accessibleTitle = options.accessibleTitle ?? '';
    this.accessibleHint = options.accessibleHint ?? options.accessibleTitle ?? '';
    this.tabIndex = options.tabIndex ?? 0;
    this.cursor = 'pointer';
    this.on('pointertap', () => {
      if (!this.isEnabled || !this.handler) return;
      this.activationHandler?.();
      this.handler();
    });
    this.on('pointerover', () => this.setPointerState(true, this.isPressed));
    this.on('pointerout', () => this.setPointerState(false, false));
    this.on('pointerdown', () => this.setPointerState(this.isHovered, true));
    this.on('pointerup', () => this.setPointerState(this.isHovered, false));
    this.on('pointerupoutside', () => this.setPointerState(false, false));
  }

  onTap(handler: () => void): this {
    this.handler = handler;
    return this;
  }

  onActivated(handler: () => void): this {
    this.activationHandler = handler;
    return this;
  }

  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
    if (!enabled) {
      this.isHovered = false;
      this.isPressed = false;
    }
    this.eventMode = enabled ? 'static' : 'none';
    this.cursor = enabled ? 'pointer' : 'default';
    this.alpha = enabled ? 1 : 0.45;
    this.syncVisualState();
    this.syncAccessibility();
  }

  /** Render-only latched press treatment for an action that remains active asynchronously. */
  setActive(active: boolean): void {
    this.isActive = active;
    this.syncVisualState();
  }

  setAccessibilityEnabled(enabled: boolean): void {
    this.accessibilityEnabled = enabled;
    this.syncAccessibility();
  }

  /** Responsive geometry scale kept separate from transient hover/press feedback. */
  setLayoutScale(scale: number): void {
    this.layoutScale = Number.isFinite(scale) && scale > 0 ? scale : 1;
    this.syncVisualState();
  }

  /** Art-directed icon scale within the unchanged physical button and hit area. */
  setContentScale(scale: number): void {
    this.contentScale = Number.isFinite(scale) && scale > 0 ? scale : 1;
    this.syncVisualState();
  }

  setDropShadowVisible(visible: boolean): void {
    this.dropShadow.visible = visible;
  }

  setBackgroundTexture(texture: Texture | undefined): void {
    this.hasBackgroundTexture = Boolean(texture);
    this.bg.visible = !texture;
    this.backgroundSprite.visible = Boolean(texture);
    if (!texture) return;
    this.backgroundSprite.texture = texture;
    this.backgroundSprite.width = this.diameter * BACKGROUND_ART_COVER_SCALE;
    this.backgroundSprite.height = this.diameter * BACKGROUND_ART_COVER_SCALE;
  }

  setTexture(texture: Texture | undefined): void {
    if (!texture) {
      this.sprite.visible = false;
      this.glyphText.visible = true;
      return;
    }
    this.sprite.texture = texture;
    this.sprite.width = this.diameter * this.iconScaleRatio;
    this.sprite.height = this.diameter * this.iconScaleRatio;
    this.sprite.visible = true;
    this.glyphText.visible = false;
  }

  get debugUsesBackgroundTexture(): boolean {
    return this.hasBackgroundTexture;
  }

  get debugUsesTexture(): boolean {
    return this.sprite.visible;
  }

  get debugDropShadowVisible(): boolean {
    return this.dropShadow.visible;
  }

  debugIconSize(): { width: number; height: number } {
    return { width: round2(this.sprite.width), height: round2(this.sprite.height) };
  }

  debugIconPosition(): { x: number; y: number } {
    return { x: this.sprite.position.x, y: this.sprite.position.y };
  }

  debugGlyphPosition(): { x: number; y: number } {
    return { x: this.glyphText.position.x, y: this.glyphText.position.y };
  }

  debugInteractionState(): { hovered: boolean; pressed: boolean; scale: number; tint: number } {
    return {
      hovered: this.isHovered,
      pressed: this.isPressed,
      scale: round2(this.scale.x),
      tint: this.backgroundSprite.visible ? this.backgroundSprite.tint : this.bg.tint,
    };
  }

  private setPointerState(hovered: boolean, pressed: boolean): void {
    if (!this.isEnabled) return;
    this.isHovered = hovered;
    this.isPressed = pressed;
    this.syncVisualState();
  }

  private syncVisualState(): void {
    const interactionScale = this.isPressed ? 0.95 : this.isHovered ? 1.04 : 1;
    const activeScale = this.isActive ? this.activeIconScaleRatio : 1;
    const tint = this.isPressed
      ? 0xd8cef5
      : this.isActive
        ? this.activeIconTint
        : this.isHovered
          ? 0xfff1cf
          : 0xffffff;
    this.scale.set(this.layoutScale * interactionScale);
    this.contentLayer.scale.set(this.contentScale * activeScale);
    this.bg.tint = tint;
    this.backgroundSprite.tint = tint;
  }

  private syncAccessibility(): void {
    this.accessible =
      this.hasAccessibleLabel && this.isEnabled && this.accessibilityEnabled && this.visible;
  }
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}
